import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import IdentityVerificationModal from "@/components/auth/IdentityVerificationModal";
import CardSetupModal from "@/components/auth/CardSetupModal";
import { IS_DEV, isStripeIdentityBypassed } from "@/lib/environment";
import { isBrowserSupported } from "@/lib/browser-support";

/**
 * OnboardingGates -- Secuencia obligatoria para usuarios del Marketplace.
 * Flujo:
 * 1. Verificación de Identidad (DNI + biometría/selfie) para todos los usuarios.
 * 2. Mandato de Tarjeta (débito o crédito para cargos de alquiler / fianza).
 *
 * *Eliminado:* Pasarela y cobro de membresía inicial y recurrente.
 */
export default function OnboardingGates() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState<"identity" | "card" | "pending" | null>("pending");
  const [browserBannerDismissed, setBrowserBannerDismissed] = useState(false);

  const profileLoaded = !!profile;
  const isDev = IS_DEV;

  // Identity -- verificación obligatoria de identidad y biometría para todos los usuarios
  const identityBypassed = isStripeIdentityBypassed();
  const identityVerified = profile?.identity_verified === true && (profile?.identity_biometric_verified === true || isDev);
  const needsIdentity = !identityBypassed && !identityVerified;

  // Card -- tarjeta guardada (débito o crédito) para formalizar reservas y fianzas
  const hasPaymentMethod = !!profile?.stripe_payment_method_id;
  const profileComplete = profile?.profile_completed === true;

  useEffect(() => {
    if (!profileLoaded) return;

    if (!profileComplete) {
      setPhase(null);
      return;
    }

    // 1. Identidad obligatoria para todos
    if (needsIdentity && !isDev) {
      setPhase("identity");
      return;
    }

    // 2. Tarjeta (débito o crédito)
    if (!hasPaymentMethod) {
      setPhase("card");
      return;
    }

    setPhase(null);
  }, [profileLoaded, isDev, profileComplete, needsIdentity, hasPaymentMethod]);

  useEffect(() => {
    if (phase === null && profileLoaded) {
      const currentPath = window.location.pathname;
      const publicRoutes = ['/catalogo', '/regalar', '/mensajes', '/sobre-nosotros', '/contacto', '/preguntas-frecuentes', '/faq', '/privacidad', '/terminos-servicio', '/terminos_servicio', '/terminos-y-condiciones', '/cookies', '/aviso-legal', '/proteccion-sets'];
      const isPublicRoute =
        publicRoutes.includes(currentPath) ||
        currentPath === '/' ||
        currentPath.startsWith('/set/') ||
        currentPath.startsWith('/catalogo/');
      if (!isPublicRoute && !currentPath.startsWith('/dashboard') && !currentPath.startsWith('/admin')) {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [phase, profileLoaded, navigate]);

  const browserUnsupported = !IS_DEV && !isBrowserSupported();
  const showBrowserBanner =
    browserUnsupported &&
    !browserBannerDismissed &&
    (phase !== null && phase !== "pending" || location.pathname.startsWith("/dashboard"));

  return (
    <>
      {showBrowserBanner && (
        <div
          role="alert"
          className="fixed bottom-4 left-1/2 z-[70] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 shadow-lg"
        >
          <div className="flex items-start gap-2">
            <span className="text-base leading-none">⚠️</span>
            <p className="flex-1 leading-snug">
              Tu navegador puede ser antiguo y algunas funciones podrían no funcionar
              correctamente. Actualízalo o prueba con Chrome, Safari o Firefox.
            </p>
            <button
              type="button"
              aria-label="Cerrar aviso"
              onClick={() => setBrowserBannerDismissed(true)}
              className="shrink-0 rounded px-1 text-amber-700 hover:bg-amber-100"
            >
              ✕
            </button>
          </div>
        </div>
      )}
      <IdentityVerificationModal open={phase === "identity"} />
      <CardSetupModal open={phase === "card"} />
    </>
  );
}
